import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TerminalWorkflowType } from '../api/terminals';
import useTerminal from './useTerminal';

export enum CustomizableCopyType {
    // COLLEAGUE
    COLLEAGUE = 'colleague',
    A_COLLEAGUE = 'a_colleague',
    THE_COLLEAGUE = 'the_colleague',

    // MANAGER
    MANAGER = 'manager',
    YOUR_MANAGER = 'your_manager',
}

type CustomizableCopyBase = {
    [k in CustomizableCopyType]: React.ReactChild | React.ReactFragment | React.ReactPortal;
};

type CustomizableCopySentenceCase = {
    [k in CustomizableCopyType as `${k}_sentence_case`]: React.ReactChild | React.ReactFragment | React.ReactPortal;
};

type CustomizableCopy = CustomizableCopyBase & CustomizableCopySentenceCase;

const defaultCustomizableCopy: CustomizableCopy = {
    colleague: (
        <FormattedMessage
            id='CustomizableCopy.colleague'
            description='Customizable copy default for "colleague" (lowercase)'
            defaultMessage='colleague'
        />
    ),
    colleague_sentence_case: (
        <FormattedMessage
            id='CustomizableCopy.colleague_sentence_case'
            description='Customizable copy default for "colleague" (sentence case)'
            defaultMessage='Colleague'
        />
    ),
    a_colleague: (
        <FormattedMessage
            id='CustomizableCopy.a_colleague'
            description='Customizable copy default for "a colleague" (lowercase)'
            defaultMessage='a colleague'
        />
    ),
    a_colleague_sentence_case: (
        <FormattedMessage
            id='CustomizableCopy.a_colleague_sentence_case'
            description='Customizable copy default for "a colleague" (sentence case)'
            defaultMessage='A colleague'
        />
    ),
    the_colleague: (
        <FormattedMessage
            id='CustomizableCopy.the_colleague'
            description='Customizable copy default for "the colleague" (lowercase)'
            defaultMessage='the colleague'
        />
    ),
    the_colleague_sentence_case: (
        <FormattedMessage
            id='CustomizableCopy.the_colleague_sentence_case'
            description='Customizable copy default for "the colleague" (sentence case)'
            defaultMessage='The colleague'
        />
    ),
    manager: (
        <FormattedMessage
            id='CustomizableCopy.manager'
            description='Customizable copy default for "manager" (lowercase)'
            defaultMessage='manager'
        />
    ),
    manager_sentence_case: (
        <FormattedMessage
            id='CustomizableCopy.manager_sentence_case'
            description='Customizable copy default for "manager" (sentence case)'
            defaultMessage='Manager'
        />
    ),
    your_manager: (
        <FormattedMessage
            id='CustomizableCopy.your_manager'
            description='Customizable copy default for "your manager" (lowercase)'
            defaultMessage='your manager'
        />
    ),
    your_manager_sentence_case: (
        <FormattedMessage
            id='CustomizableCopy.your_manager_sentence_case'
            description='Customizable copy default for "your manager" (sentence case)'
            defaultMessage='Your manager'
        />
    ),
};

export default function useCustomizableCopy(): CustomizableCopy & CustomizableCopySentenceCase {
    const terminal = useTerminal();
    if (!terminal || terminal.workflow !== TerminalWorkflowType.IMES_DEXIS_NIKE) {
        return defaultCustomizableCopy;
    }
    return {
        ...defaultCustomizableCopy,
        ...{
            colleague: (
                <FormattedMessage
                    id='CustomizableCopy.nike.colleague'
                    description='Customizable copy Nike custom for "colleague" (lowercase)'
                    defaultMessage='athlete'
                />
            ),
            colleague_sentence_case: (
                <FormattedMessage
                    id='CustomizableCopy.nike.colleague_sentence_case'
                    description='Customizable copy Nike custom for "colleague" (sentence case)'
                    defaultMessage='Athlete'
                />
            ),
            a_colleague: (
                <FormattedMessage
                    id='CustomizableCopy.nike.a_colleague'
                    description='Customizable copy Nike custom for "a colleague" (lowercase)'
                    defaultMessage='an athlete'
                />
            ),
            a_colleague_sentence_case: (
                <FormattedMessage
                    id='CustomizableCopy.nike.a_colleague_sentence_case'
                    description='Customizable copy Nike custom for "a colleague" (sentence case)'
                    defaultMessage='An athlete'
                />
            ),
            the_colleague: (
                <FormattedMessage
                    id='CustomizableCopy.nike.the_colleague'
                    description='Customizable copy Nike custom for "the colleague" (lowercase)'
                    defaultMessage='the athlete'
                />
            ),
            the_colleague_sentence_case: (
                <FormattedMessage
                    id='CustomizableCopy.nike.the_colleague_sentence_case'
                    description='Customizable copy Nike custom for "the colleague" (sentence case)'
                    defaultMessage='The athlete'
                />
            ),
            manager: (
                <FormattedMessage
                    id='CustomizableCopy.nike.manager'
                    description='Customizable copy Nike custom for "manager" (lowercase)'
                    defaultMessage='team coach'
                />
            ),
            manager_sentence_case: (
                <FormattedMessage
                    id='CustomizableCopy.nike.manager_sentence_case'
                    description='Customizable copy Nike custom for "manager" (sentence case)'
                    defaultMessage='Team coach'
                />
            ),
            your_manager: (
                <FormattedMessage
                    id='CustomizableCopy.nike.your_manager'
                    description='Customizable copy Nike custom for "your manager" (lowercase)'
                    defaultMessage='your team coach'
                />
            ),
            your_manager_sentence_case: (
                <FormattedMessage
                    id='CustomizableCopy.nike.your_manager_sentence_case'
                    description='Customizable copy Nike custom for "your manager" (sentence case)'
                    defaultMessage='Your team coach'
                />
            ),
        },
    };
}
